import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faChevronUp, faChevronDown, faFlask } from '@fortawesome/free-solid-svg-icons';
import examTimerService from '../../services/examTimerService';

/**
 * Exam timer component that displays total exam time from examTimerService
 * This is separate from question-level timing and tracks the entire exam session
 */
const ExamTimer = ({ 
  isRunning = false, 
  isDarkMode,
  themeStyles,
  sessionSetup,
  examData,
  onEndSession,
  isSessionEnding,
  showLabValueSidebar,
  onToggleLabValueSidebar
}) => {  const [totalElapsedTime, setTotalElapsedTime] = useState(0);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isInfoCollapsed, setIsInfoCollapsed] = useState(false);

  // Update display every second by getting current time from service
  useEffect(() => {
    const interval = setInterval(() => {
      const currentTime = examTimerService.getCurrentTimeSpent();
      setTotalElapsedTime(currentTime);
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Initialize display with current service time
  useEffect(() => {
    setTotalElapsedTime(examTimerService.getCurrentTimeSpent());
  }, []);

  // Format seconds to HH:MM:SS for exam-level timing
  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    }}>      {/* Exam Timer Container */}
      <div style={{
        minWidth: 160,
        maxWidth: 280,
        background: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(249, 250, 251, 0.95)',
        borderRadius: '12px',
        marginRight: 24,
        boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 16px rgba(0, 0, 0, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        zIndex: 2,
        position: 'relative',
        flex: isCollapsed ? '0 0 auto' : '0 0 200px',
        padding: isCollapsed ? '16px 12px 12px 12px' : '16px 12px',
        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
        transition: 'all 0.3s ease',
        minHeight: isCollapsed ? 'auto' : '200px',
        maxHeight: isCollapsed ? '60px' : 'none',
        overflow: 'hidden'
      }}>{/* Timer header */}
        <div style={{ 
          fontWeight: 600, 
          color: isDarkMode ? '#e2e8f0' : '#334155', 
          fontSize: 16, 
          textAlign: 'center', 
          letterSpacing: '0.3px',
          marginBottom: '12px',
          borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
          paddingBottom: '8px',
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>Exam Time</span>
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            style={{
              background: 'none',
              border: 'none',
              color: isDarkMode ? '#e2e8f0' : '#334155',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
            title={isCollapsed ? 'Expand timer' : 'Collapse timer'}
          >
            <FontAwesomeIcon icon={isCollapsed ? faChevronDown : faChevronUp} size="sm" />
          </button>
        </div>        
        {/* Timer display */}
        <div style={{ 
          display: isCollapsed ? 'none' : 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          gap: '8px',
          width: '100%',
          overflow: 'hidden',
          transition: 'all 0.3s ease'
        }}>          <FontAwesomeIcon 
            icon={faClock} 
            style={{ 
              fontSize: '24px',
              color: examData?.status === 'Finished'
                ? (isDarkMode ? '#f59e0b' : '#d97706')
                : isRunning 
                  ? (isDarkMode ? '#10b981' : '#059669') 
                  : (isDarkMode ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)')
            }} 
          />
          
          <div style={{
            fontSize: '18px',
            fontWeight: 700,
            color: isDarkMode ? '#f1f5f9' : '#1e293b',
            textAlign: 'center',
            fontFamily: 'monospace',
            background: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.8)',
            padding: '8px 12px',
            borderRadius: '6px',
            border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
            minWidth: '100px'
          }}>
            {formatTime(totalElapsedTime)}
          </div>
            {/* Status indicator */}
          <div style={{
            fontSize: '12px',
            color: examData?.status === 'Finished'
              ? (isDarkMode ? '#f59e0b' : '#d97706')
              : isRunning 
                ? (isDarkMode ? '#10b981' : '#059669')
                : (isDarkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)'),
            textAlign: 'center',
            fontWeight: 500
          }}>
            {examData?.status === 'Finished' 
              ? '◆ Finished' 
              : isRunning 
                ? '● Running' 
                : '○ Paused'
            }
          </div>
        </div>
      </div>      {/* Exam Info Container */}
      <div style={{
        minWidth: 160,
        maxWidth: 280,
        background: isDarkMode ? 'rgba(30, 41, 59, 0.95)' : 'rgba(249, 250, 251, 0.95)',
        borderRadius: '12px',
        marginRight: 24,
        boxShadow: isDarkMode ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 4px 16px rgba(0, 0, 0, 0.08)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        zIndex: 2,
        position: 'relative',
        flex: '0 0 auto',
        padding: isInfoCollapsed ? '16px 12px 12px 12px' : '16px 12px',
        border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.05)',
        transition: 'all 0.3s ease',
        minHeight: isInfoCollapsed ? 'auto' : 'auto',
        maxHeight: isInfoCollapsed ? '60px' : 'none',
        overflow: 'hidden'
      }}>{/* Exam Info header */}
        <div style={{ 
          fontWeight: 600, 
          color: isDarkMode ? '#e2e8f0' : '#334155', 
          fontSize: 16, 
          textAlign: 'center', 
          letterSpacing: '0.3px',
          marginBottom: '12px',
          borderBottom: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
          paddingBottom: '8px',
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>Exam Info</span>
          <button
            onClick={() => setIsInfoCollapsed(!isInfoCollapsed)}
            style={{
              background: 'none',
              border: 'none',
              color: isDarkMode ? '#e2e8f0' : '#334155',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'background-color 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
            title={isInfoCollapsed ? 'Expand exam info' : 'Collapse exam info'}
          >
            <FontAwesomeIcon icon={isInfoCollapsed ? faChevronDown : faChevronUp} size="sm" />
          </button>
        </div>        
        {/* Exam info content */}
        <div style={{ 
          display: isInfoCollapsed ? 'none' : 'flex', 
          flexDirection: 'column',
          gap: '12px',
          width: '100%',
          alignItems: 'center',
          overflow: 'hidden',
          transition: 'all 0.3s ease'
        }}>
          {/* Exam Title */}
          {(sessionSetup?.title || examData?.title) && (
            <div style={{
              fontSize: '14px',
              fontWeight: 600,
              color: isDarkMode ? '#f1f5f9' : '#1e293b',
              textAlign: 'center',
              padding: '8px 12px',
              background: isDarkMode ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.8)',
              borderRadius: '6px',
              border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)',
              width: '100%',
              wordWrap: 'break-word'
            }}>
              {sessionSetup?.title || examData?.title}
            </div>
          )}
          
          {/* Exam Type */}
          {(sessionSetup?.examType || examData?.type) && (
            <div style={{
              fontSize: '12px',
              fontWeight: 500,
              color: isDarkMode ? '#94a3b8' : '#64748b',
              textAlign: 'center',
              padding: '6px 10px',
              background: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
              borderRadius: '4px',
              border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.1)'
            }}>
              {sessionSetup?.examType || examData?.type}
            </div>
          )}
            {/* Lab Value Button */}
            {onToggleLabValueSidebar && (
              <button
                className="icon-button lab-value-button"
                onClick={onToggleLabValueSidebar}
                aria-pressed={showLabValueSidebar}
                style={{
                  width: '100%',
                  marginBottom: '8px'
                }}
                title="Show Lab Values Reference"
              >
                <FontAwesomeIcon icon={faFlask} />
                Lab Values
              </button>
            )}
            {/* End Session Button */}
          {onEndSession && (
            <button
              onClick={onEndSession}
              disabled={isSessionEnding || examData?.status === 'Finished'}
              style={{
                background: isDarkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.08)',
                border: '1px solid ' + (isDarkMode ? 'rgba(239, 68, 68, 0.5)' : 'rgba(239, 68, 68, 0.3)'),
                color: isDarkMode ? '#fca5a5' : '#ef4444',
                padding: '8px 16px',
                fontSize: '12px',
                fontWeight: '500',
                borderRadius: '6px',
                cursor: (isSessionEnding || examData?.status === 'Finished') ? 'not-allowed' : 'pointer',
                opacity: (isSessionEnding || examData?.status === 'Finished') ? 0.6 : 1,
                transition: 'all 0.2s ease',
                width: '100%'
              }}
            >
              {examData?.status === 'Finished' ? 'Session Finished' : (isSessionEnding ? 'Ending...' : 'End Session')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExamTimer;
